import React from "react";
import DOMPurify from "dompurify";
import { safeJSONParse } from "../../../utils/helper";
import styles from "../agent-response.module.scss";
import { Button, Box, Divider, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

type EmailDraftingAgentProps = {
  parsedContent: any;
};

DOMPurify.addHook("afterSanitizeAttributes", function (node) {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export default function EmailDraftingAgent({
  parsedContent,
}: EmailDraftingAgentProps): JSX.Element {
  const answer = safeJSONParse(parsedContent?.answer);
  const question = parsedContent?.question;

  const emails = answer.draft_emails || [];

  // Handle send single email - Open Outlook with pre-filled draft
  const handleSendEmail = (email: any) => {
    try {
      // Helper function to strip HTML tags but preserve link URLs
      const stripHtml = (html: string) => {
        const tmp = document.createElement("div");
        tmp.innerHTML = html;

        // Find all anchor tags and replace them with their href URLs
        const anchors = tmp.querySelectorAll("a");
        anchors.forEach((anchor) => {
          const href = anchor.getAttribute("href");
          if (href) {
            // Replace the anchor with just the URL
            const textNode = document.createTextNode(href);
            anchor.parentNode?.replaceChild(textNode, anchor);
          }
        });

        return tmp.textContent || tmp.innerText || "";
      };

      // Process body paragraphs - strip HTML and join with double line breaks
      const bodyParagraphsText = (email.body_paragraphs || [])
        .map((p: string) => stripHtml(p))
        .join("\n\n");

      // Construct email body with proper spacing
      const bodyParts = [];

      if (email.greeting) bodyParts.push(email.greeting);
      if (bodyParagraphsText) bodyParts.push(bodyParagraphsText);

      // Combine closing and signature without blank line between them
      const closingPart = [];
      if (email.closing) closingPart.push(email.closing);
      if (email.signature_block) closingPart.push(email.signature_block);
      if (closingPart.length > 0) bodyParts.push(closingPart.join("\n"));

      const bodyText = bodyParts.join("\n\n");

      // Encode subject and body for URL
      const subject = encodeURIComponent(email.subject || "");
      const body = encodeURIComponent(bodyText);
      const recipient = encodeURIComponent(email.email || "");

      // Create mailto link
      const mailtoLink = `mailto:${recipient}?subject=${subject}&body=${body}`;

      // Use iframe approach to open mailto link
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = mailtoLink;
      document.body.appendChild(iframe);

      // Clean up iframe after a short delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);

      console.log(
        `Successfully opened email draft for ${email.candidate_name} in your default email client`
      );
    } catch (error) {
      console.error("Error opening email draft:", error);
      alert(
        "There was an error opening your email client. Please check your default email application settings."
      );
    }
  };

  return (
    <div
      style={{
        color: "#333",
        width: "100%",
      }}
    >
      <Typography
        variant="h5"
        style={{ marginBottom: "20px", fontWeight: 600 }}
      >
        {answer.title}
      </Typography>
      {answer.selected_candidates_count && (
        <div style={{ marginBottom: "20px", color: "#00d2aa" }}>
          <strong>Selected Candidates:</strong>{" "}
          {answer.selected_candidates_count}
        </div>
      )}

      {/* Email List */}
      {emails.map((email: any, idx: number) => (
        <Box
          key={idx}
          sx={{
            border: "1px solid #ddd",
            borderRadius: "8px",
            padding: 2.5,
            marginBottom: 2.5,
            backgroundColor: "#fafafa",
            transition: "all 0.2s ease-in-out",
            position: "relative",
            "&:hover": {
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          {/* Candidate Header */}
          <Box
            sx={{
              marginBottom: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {email.candidate_name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {email.email}
              </Typography>
            </Box>

            {/* Send Button - Top Right */}
            <Button
              variant="contained"
              size="small"
              startIcon={<SendIcon />}
              onClick={() => handleSendEmail(email)}
              sx={{
                color: "#fff",
                paddingX: 2,
                paddingY: 1,
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Send Email
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Subject */}
          <Box sx={{ margin: "12px 0" }}>
            <Typography variant="body2" component="span" fontWeight={600}>
              Subject:{" "}
            </Typography>
            <Typography variant="body2" component="span">
              {email.subject}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Greeting */}
          <Typography
            variant="body2"
            sx={{ margin: "12px 0", fontSize: "14px", fontWeight: 200 }}
          >
            {email.greeting}
          </Typography>
          {/* Body Paragraphs */}
          {/* Body Paragraphs */}
          <Box
            sx={{
              margin: "12px 0",

              // 🔥 LINK STYLING — THIS IS THE KEY
              "& a": {
                color: "#0077b5",
                textDecoration: "underline",
                fontSize: "14px",
                fontWeight: 200,
                cursor: "pointer",
              },
              "& a:hover": {
                textDecoration: "none",
                opacity: 0.85,
              },
              // Ensure consistent font size and weight for all paragraph content
              "& p": {
                fontSize: "14px",
                fontWeight: 200,
                margin: "8px 0",
                lineHeight: 1.6,
              },
            }}
          >
            {(email.body_paragraphs || []).map((p: string, pIdx: number) => {
              // Sanitize HTML with DOMPurify
              let sanitized = DOMPurify.sanitize(p);

              // Remove stray < and > symbols that might remain after sanitization
              // This regex removes standalone < or > that aren't part of valid HTML tags
              sanitized = sanitized.replace(/<(?![a-zA-Z\/!])/g, '&lt;');
              sanitized = sanitized.replace(/(?<![a-zA-Z0-9"'])>/g, '&gt;');

              // Additional cleanup: remove any remaining empty tags like <> or < >
              sanitized = sanitized.replace(/<\s*>/g, '');
              sanitized = sanitized.replace(/&lt;\s*&gt;/g, '');

              return (
                <Box
                  key={pIdx}
                  sx={{ margin: "8px 0", lineHeight: 1.6, fontSize: "14px", fontWeight: 200 }}
                  dangerouslySetInnerHTML={{
                    __html: sanitized,
                  }}
                />
              );
            })}
          </Box>

          {/* Closing */}
          <Typography
            variant="body2"
            sx={{ margin: "12px 0", fontSize: "14px", fontWeight: 200 }}
          >
            {email.closing}
          </Typography>

          {/* Signature */}
          <Typography
            variant="body2"
            sx={{ marginTop: "12px", fontSize: "14px", fontWeight: 200 }}
          >
            {email.signature_block}
          </Typography>
        </Box>
      ))}

      <div className={styles.finalQuestion}>{question}</div>
    </div>
  );
}
